<script setup lang="ts">
    import { type FormKitNode } from '@formkit/core';
    import Skeleton from '@shared/components/ui/skeleton.vue';
    import { FormSizeTypeEnum, FormStyleEnum } from '@shared/constants/form-style';
    import { type TimeInputBlurEventTyping } from '@shared/typings';

    const props = defineProps<{
        modelValue?: string
        id?: string
        name?: string
        label?: string
        placeholder?: string
        validation?: string | [rule: string, ...args: unknown[]][]
        help?: string
        style?: FormStyleEnum.PRIMARY | FormStyleEnum.SECONDARY
        size?: FormSizeTypeEnum.NORMAL | FormSizeTypeEnum.EXTRA
        suffixIcon?: string,
        prefixIcon?: string,
        disabled?: boolean,
        isDecimal?: boolean,
        labelHidden?: boolean,
        validationVisibility?: 'submit' | 'live' | 'blur' | 'dirty' | undefined,
        validationRules?: Record<string, (node: FormKitNode, ...args: any[]) => boolean | Promise<boolean>>, // eslint-disable-line @typescript-eslint/no-explicit-any, func-call-spacing
        validationMessages?: Record<string, string>
        isLoading?: boolean
        autofocus?: boolean
        ignore?: boolean
        value?: string
    }>();

    // eslint-disable-next-line func-call-spacing
    const emit = defineEmits<{
        (event: 'suffixIconClick' | 'enter'),
        (event: 'change' | 'blur', value: TimeInputBlurEventTyping),
        (event: 'update:modelValue', value: string),
    }>();

    const valueModel: Ref<string> = ref(props.modelValue ?? '');

    const isNumerical = computed(() =>
        Array.isArray(props.validation) ? props.validation[0].includes('number') : props.validation?.includes('number'));

    const isDecimalNumber = computed(() => {
        // Prop indicates that it's a decimal
        if (props.isDecimal) return true;
        if (isNumerical.value) {
            // FormKit validation indicates that it is a decimal
            if (Array.isArray(props.validation)) {
                return props.validation[0].includes('float');
            }
            return props.validation?.includes('float');
        }
        return false;
    });

    const isRequired = computed(() =>
        Array.isArray(props.validation) ? props.validation[0].includes('required') : props.validation?.includes('required'));

    // Sync prop modelValue with internal valueModel
    watch(() => props.modelValue, newValue => {
        if (newValue !== valueModel.value) {
            valueModel.value = newValue ?? '';
        }
    });

    // Emit updated value when valueModel changes
    watch(valueModel, newValue => {
        emit('update:modelValue', newValue);
    });
</script>

<template>
    <div
        :data-required="isRequired"
        class="time-input"
        data-constell="constell-time-input"
    >
        <Skeleton
            v-if="isLoading"
            height="60px"
        />
        <FormKit
            v-else
            :id="id ?? name ?? 'time'"
            v-model="valueModel"
            :label="label"
            :name="name"
            :placeholder="placeholder"
            :validation-rules="validationRules"
            :validation-messages="validationMessages"
            :validation="
                isDecimalNumber
                    ? `${validation}|validateDecimalFormat`
                    : validation"
            :validation-visibility="validationVisibility"
            :help="help"
            :inner-class="style ?? FormStyleEnum.PRIMARY"
            :wrapper-class="size ?? FormSizeTypeEnum.NORMAL"
            :suffix-icon="suffixIcon"
            :suffix-icon-class="suffixIcon === 'removeOutlined' ? 'formkit-icon--clickable formkit-icon--negative' : ''"
            :prefix-icon="prefixIcon"
            :disabled="disabled"
            :label-class="labelHidden ? 'formkit-label--hidden' : ''"
            :autofocus="autofocus"
            :value="String(value)"
            :ignore="ignore"
            type="time"
            data-constell="constell-time-input-change"
            @suffix-icon-click="$emit('suffixIconClick')"
            @change="$emit('change', $event)"
            @blur="$emit('blur', $event)"
            @keydown.enter="$emit('enter')"
        />
    </div>
</template>

<style scoped lang="scss">
    .time-input :deep(.formkit-outer) {
        .formkit-inner {
            input {
                display: flex;
                box-sizing: border-box;
                align-items: center;
                justify-content: center;
                padding: var(--spacing-03);
                border: 1px solid var(--border-tertiary);
                border-radius: var(--border-radius-xs);
                background-color: var(--background-form-input);
                block-size: var(--size-11);
                font-size: var(--font-regular);
                inline-size: 100%;
                line-height: 1;

                &::placeholder {
                    color: var(--text-placeholder);
                }

                &::-webkit-calendar-picker-indicator {
                    display: none;
                }

                &::-webkit-date-and-time-value {
                    margin: 0;
                    text-align: inherit;
                }

                &::-webkit-datetime-edit {
                    padding: 0;
                }

                &:hover,
                &:focus,
                &:focus-visible,
                &:focus-within {
                    background-color: var(--background-form-input-active);
                }

                @include min-width(md) {
                    block-size: var(--size-12);
                }

                @include min-width(lg) {
                    block-size: var(--size-13);
                }
            }

            &.secondary input {
                &:hover {
                    background-color: var(--background-form-input-hover);
                }

                &:focus,
                &:focus-visible,
                &:focus-within {
                    background-color: var(--background-form-input-active);
                }
            }
        }

        &[data-invalid=true] {
            .formkit-inner {
                input,
                &.secondary input {
                    border-color: var(--border-negative);
                }
            }
        }

        &[data-required=true] {
            .formkit-label::after {
                color: var(--text-secondary);
                content:" *";
            }
        }

        &[data-disabled=true] .formkit-inner {
            input,
            &.secondary input {
                border-color: var(--border-disabled-1);
                background-color: var(--background-disabled-2);
                color: var(--text-disabled-1);
                cursor: not-allowed;
            }
        }
    }

    .time-input[data-required=true] :deep(.formkit-outer) {
        .formkit-label::after {
            color: var(--text-secondary);
            content:" *";
        }
    }
</style>
